import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ChevronsLeft, ChevronsRight, LogOut, Search, Settings, X, User, Users, Building2, Store, UserCircle, BellRing } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as Reselect from 'reselect';
import { processMenuData } from './LayoutMenuData';
import { scrollSidebarToModule } from '../utils/sidebarUtils';
import { customerService } from '../../modules/master-data/Customer/services/customer.service';
import { CUSTOMER_MAIN_TYPE_ID } from '../constants';
import Lottie from 'lottie-react';

import defaultUserProfile from "assets/images/users/user-profile.png";
import defaultLogoSm from "assets/images/logo/logo-sm.png";
import defaultLogoDark from "assets/images/logo/logo-dark.png";
import defaultLogoLight from "assets/images/logo/logo-light.png";

import LightDark from '../components/common/LightDark';
import NotificationDropdown from '../components/common/NotificationDropdown';
import BackgroundJobsDropdown from '../components/common/BackgroundJobsDropdown';
import UpdateButton from '../components/common/UpdateButton';
import { Dropdown, useDropdown } from '../components/common/Dropdown';
import { changeLeftsidebarSizeType } from '../store/thunk';
import { setSearchTriggeredModule, clearSearchTriggeredModule } from '../store/layouts/reducer';
import profileAnimation from '../../assets/animations/profile.json';


// Helper function to flatten menu items for search
const flattenMenuItems = (items: any[], parentLabel: string = ''): any[] => {
    let flattened: any[] = [];
    (items || []).forEach(item => {
        const fullLabel = parentLabel ? `${parentLabel} > ${item.label}` : item.label;

        // Skip excluded modules from search
        const moduleName = (item.label || '').toLowerCase();

        // Include item if it has a link OR subItems (to allow searching for parent modules)
        if (item.link || (item.subItems && item.subItems.length > 0)) {
            flattened.push({
                ...item,
                fullLabel,
                searchLabel: item.label.toLowerCase(),
            });
        }
        if (item.subItems && Array.isArray(item.subItems)) {
            flattened = [...flattened, ...flattenMenuItems(item.subItems, fullLabel)];
        }
    });
    return flattened;
};

const ProfileLink = ({ className, children }: any) => {
    const { setOpen } = useDropdown();
    return (
        <Link
            to="/user-profile"
            className={className}
            onClick={() => setOpen(false)}
        >
            {children}
        </Link>
    );
};

const Header = ({ handleToggleDrawer, handleDrawer, handleToggleNotification }: any) => {

    const dispatch = useDispatch<any>();
    const navigate = useNavigate();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [activeItem, setActiveItem] = useState<any>(null); // For secondary view
    const [selectedSubItemIndex, setSelectedSubItemIndex] = useState(0); // For sub-items navigation
    const [focusOnSubItems, setFocusOnSubItems] = useState(false); // Track if focus is on sub-items panel
    const searchInputRef = useRef<HTMLInputElement>(null);
    const searchDropdownRef = useRef<HTMLDivElement>(null);

    // Customer search suggestions state
    const [customerSuggestions, setCustomerSuggestions] = useState<any[]>([]);
    const [isLoadingParties, setIsLoadingParties] = useState(false);
    const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1); // -1 means no customer selected
    const [focusOnParties, setFocusOnParties] = useState(false); // Track if focus is on customer suggestions
    const customerSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // --- New: Get menuData from Redux store ---
    const selectMenuData = (state: any) => state.Layout.menuData;

    // Create a custom selector with deep equality check for the menu data
    const createDeepEqualSelector = Reselect.createSelectorCreator(Reselect.lruMemoize, (a: any, b: any) => JSON.stringify(a) === JSON.stringify(b));

    const selectProcessedMenuData = createDeepEqualSelector(
        [selectMenuData],
        (menuData) => processMenuData(menuData || [])
    );

    const processedMenuData = useSelector(selectProcessedMenuData);

    // Flatten menu items for search
    const allMenuItems = flattenMenuItems(processedMenuData);

    // Helper function to get customer detail route based on customerMainTypeId
    const getCustomerDetailRoute = useCallback((customerMainTypeId: number, customerId: string) => {
        switch (customerMainTypeId) {
            case CUSTOMER_MAIN_TYPE_ID.PRIMARY:
                return `/customer/primary-network/details/${customerId}`;
            case CUSTOMER_MAIN_TYPE_ID.SECONDARY:
                return `/customer/secondary-network/details/${customerId}`;
            case CUSTOMER_MAIN_TYPE_ID.TERTIARY:
                return `/customer/direct-network/details/${customerId}`;
            case CUSTOMER_MAIN_TYPE_ID.INFLUENCER:
                return `/customer/influencer-network/details/${customerId}`;
            default:
                return `/customer/primary-network/details/${customerId}`;
        }
    }, []);

    // Helper function to get customer type label
    const getCustomerTypeLabel = useCallback((customerMainTypeId: number) => {
        switch (customerMainTypeId) {
            case CUSTOMER_MAIN_TYPE_ID.PRIMARY:
                return 'Primary';
            case CUSTOMER_MAIN_TYPE_ID.SECONDARY:
                return 'Secondary';
            case CUSTOMER_MAIN_TYPE_ID.TERTIARY:
                return 'Tertiary';
            case CUSTOMER_MAIN_TYPE_ID.INFLUENCER:
                return 'Influencer';
            default:
                return 'Customer';
        }
    }, []);

    // Helper function to get customer type icon
    const getCustomerTypeIcon = useCallback((customerMainTypeId: number) => {
        switch (customerMainTypeId) {
            case CUSTOMER_MAIN_TYPE_ID.PRIMARY:
                return <Building2 className="size-4" />;
            case CUSTOMER_MAIN_TYPE_ID.SECONDARY:
                return <Store className="size-4" />;
            case CUSTOMER_MAIN_TYPE_ID.TERTIARY:
                return <Users className="size-4" />;
            case CUSTOMER_MAIN_TYPE_ID.INFLUENCER:
                return <UserCircle className="size-4" />;
            default:
                return <Users className="size-4" />;
        }
    }, []);

    // Debounced customer search function
    const searchParties = useCallback(async (query: string) => {
        if (!query || query.trim().length < 2) {
            setCustomerSuggestions([]);
            return;
        }

        setIsLoadingParties(true);
        try {
            const suggestions = await customerService.searchcustomerSuggestions(query.trim(), 8);
            setCustomerSuggestions(suggestions);
        } catch (error) {
            console.error('Failed to fetch customer suggestions:', error);
            setCustomerSuggestions([]);
        } finally {
            setIsLoadingParties(false);
        }
    }, []);

    // react-redux
    const selectLayoutState = (state: any) => state.Layout;
    const selectLayoutProperties = Reselect.createSelector(
        selectLayoutState,
        (layout: any) => ({
            layoutSidebarSizeType: layout.layoutSidebarSizeType,
            layoutType: layout.layoutType,
        })
    );

    const { layoutSidebarSizeType, layoutType } = useSelector(selectLayoutProperties);

    // Search handlers
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        // Ensure search focus is active when typing
        if (!isSearchFocused) {
            setIsSearchFocused(true);
        }

        // Clear any existing highlight when user starts a new search
        dispatch(clearSearchTriggeredModule());
        // Reset focus states
        setFocusOnSubItems(false);
        setSelectedSubItemIndex(0);
        setFocusOnParties(false);
        setSelectedCustomerIndex(-1);

        if (!query.trim()) {
            setSearchResults([]);
            setSelectedIndex(0);
            setActiveItem(null);
            setCustomerSuggestions([]);
            return;
        }

        // Search modules
        const filtered = allMenuItems.filter(item =>
            item.searchLabel.includes(query.toLowerCase())
        );
        setSearchResults(filtered);
        setSelectedIndex(0);
        setActiveItem(filtered[0] || null);

        // Debounced customer search (only if query is at least 2 characters)
        if (customerSearchTimeoutRef.current) {
            clearTimeout(customerSearchTimeoutRef.current);
        }
        if (query.trim().length >= 2) {
            customerSearchTimeoutRef.current = setTimeout(() => {
                searchParties(query);
            }, 300);
        } else {
            setCustomerSuggestions([]);
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        const hasModuleResults = searchResults.length > 0;
        const hascustomerResults = customerSuggestions.length > 0;

        if (!hasModuleResults && !hascustomerResults) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();

            if (focusOnParties && hascustomerResults) {
                // Navigate within customer suggestions
                const newIndex = (selectedCustomerIndex + 1) % customerSuggestions.length;
                setSelectedCustomerIndex(newIndex);
            } else if (focusOnSubItems && activeItem?.subItems?.length > 0) {
                // Navigate within sub-items
                const newIndex = (selectedSubItemIndex + 1) % activeItem.subItems.length;
                setSelectedSubItemIndex(newIndex);
            } else if (hasModuleResults) {
                // Navigate within main search results
                const newIndex = (selectedIndex + 1) % searchResults.length;
                setSelectedIndex(newIndex);
                setActiveItem(searchResults[newIndex]);
                setFocusOnSubItems(false);
                setSelectedSubItemIndex(0);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();

            if (focusOnParties && hascustomerResults) {
                // Navigate within customer suggestions
                const newIndex = (selectedCustomerIndex - 1 + customerSuggestions.length) % customerSuggestions.length;
                setSelectedCustomerIndex(newIndex);
            } else if (focusOnSubItems && activeItem?.subItems?.length > 0) {
                // Navigate within sub-items
                const newIndex = (selectedSubItemIndex - 1 + activeItem.subItems.length) % activeItem.subItems.length;
                setSelectedSubItemIndex(newIndex);
            } else if (hasModuleResults) {
                // Navigate within main search results
                const newIndex = (selectedIndex - 1 + searchResults.length) % searchResults.length;
                setSelectedIndex(newIndex);
                setActiveItem(searchResults[newIndex]);
                setFocusOnSubItems(false);
                setSelectedSubItemIndex(0);
            }
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            // Shift focus to sub-items panel if current item has sub-items
            const selected = searchResults[selectedIndex];
            if (selected?.subItems && selected.subItems.length > 0) {
                setFocusOnSubItems(true);
                setFocusOnParties(false);
                setSelectedSubItemIndex(0);
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            // Shift focus back to main search results
            if (focusOnSubItems) {
                setFocusOnSubItems(false);
                setSelectedSubItemIndex(0);
            } else if (focusOnParties) {
                setFocusOnParties(false);
                setSelectedCustomerIndex(-1);
            }
        } else if (e.key === 'Tab') {
            // Tab to switch between modules and customers sections
            if (hasModuleResults && hascustomerResults) {
                e.preventDefault();
                if (focusOnParties) {
                    setFocusOnParties(false);
                    setSelectedCustomerIndex(-1);
                    setSelectedIndex(0);
                } else {
                    setFocusOnParties(true);
                    setSelectedCustomerIndex(0);
                    setFocusOnSubItems(false);
                }
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();

            if (focusOnParties && hascustomerResults && selectedCustomerIndex >= 0) {
                // Navigate to selected customer detail page
                const selectedCustomer = customerSuggestions[selectedCustomerIndex];
                if (selectedCustomer) {
                    const route = getCustomerDetailRoute(selectedCustomer.customerMainTypeId, selectedCustomer.id);
                    navigate(route);
                    clearSearch();
                }
            } else if (focusOnSubItems && activeItem?.subItems?.length > 0) {
                // Navigate to selected sub-item
                const selectedSubItem = activeItem.subItems[selectedSubItemIndex];
                if (selectedSubItem?.link) {
                    navigate(selectedSubItem.link);
                    clearSearch();

                    // Scroll sidebar to show the module
                    setTimeout(() => {
                        scrollSidebarToModule(selectedSubItem.link);
                    }, 100);
                }
            } else if (hasModuleResults) {
                // Handle main item selection
                const selected = searchResults[selectedIndex];
                if (selected) {
                    if (selected.link && (!selected.subItems || selected.subItems.length === 0)) {
                        // Navigate to sub-module directly
                        navigate(selected.link);
                        clearSearch();

                        // Scroll sidebar to show the module
                        setTimeout(() => {
                            scrollSidebarToModule(selected.link);
                        }, 100);
                    } else if (selected.subItems && selected.subItems.length > 0) {
                        // Parent module with sub-modules: shift focus to sub-items panel
                        setFocusOnSubItems(true);
                        setSelectedSubItemIndex(0);
                    }
                }
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            clearSearch();
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setIsSearchFocused(false);
        setSelectedIndex(0);
        setActiveItem(null);
        setFocusOnSubItems(false);
        setSelectedSubItemIndex(0);
        // Clear customer suggestions
        setCustomerSuggestions([]);
        setSelectedCustomerIndex(-1);
        setFocusOnParties(false);
        if (customerSearchTimeoutRef.current) {
            clearTimeout(customerSearchTimeoutRef.current);
        }
        // Don't clear the search-triggered module here, as it needs to persist
        // until the dropdown is opened and the highlight is applied
    };

    const handleSearchItemClick = (item: any) => {
        if (item.link && (!item.subItems || item.subItems.length === 0)) {
            // Navigate to sub-module directly
            navigate(item.link);
            clearSearch();

            // Scroll sidebar to show the module
            setTimeout(() => {
                scrollSidebarToModule(item.link);
            }, 100);
        } else if (item.subItems && item.subItems.length > 0) {
            // Parent module with sub-modules: shift focus to sub-items panel
            setActiveItem(item);
            setFocusOnSubItems(true);
            setSelectedSubItemIndex(0);
        }
    };

    const handleSubItemClick = (subItem: any) => {
        if (subItem.link) {
            navigate(subItem.link);
            clearSearch();

            // Scroll sidebar to show the module
            setTimeout(() => {
                scrollSidebarToModule(subItem.link);
            }, 100);
        }
    };

    const handlecustomerSuggestionClick = (customer: any) => {
        const route = getCustomerDetailRoute(customer.customerMainTypeId, customer.id);
        navigate(route);
        clearSearch();
    };

    // Cleanup customer search timeout on unmount
    useEffect(() => {
        return () => {
            if (customerSearchTimeoutRef.current) {
                clearTimeout(customerSearchTimeoutRef.current);
            }
        };
    }, []);

    // Global keyboard shortcut for search (Ctrl+K)
    // Note: Other keyboard shortcuts are handled by KeyboardShortcutListener component
    useEffect(() => {
        const handleSearchShortcut = (e: KeyboardEvent) => {
            // Only handle Ctrl+K for search focus
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                e.stopPropagation();
                searchInputRef.current?.focus();
                setIsSearchFocused(true);
            }
        };

        window.addEventListener('keydown', handleSearchShortcut);
        return () => window.removeEventListener('keydown', handleSearchShortcut);
    }, []);

    // Close search on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target as Node)) {
                setIsSearchFocused(false);
            }
        };

        if (isSearchFocused) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isSearchFocused]);

    // Auto-scroll selected sub-item into view
    useEffect(() => {
        if (focusOnSubItems && activeItem?.subItems?.length > 0) {
            // Find the selected sub-item button and scroll it into view
            setTimeout(() => {
                const subItemButtons = searchDropdownRef.current?.querySelectorAll('.w-1\\/2 button');
                if (subItemButtons && subItemButtons[selectedSubItemIndex]) {
                    (subItemButtons[selectedSubItemIndex] as HTMLElement).scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }
            }, 50);
        }
    }, [focusOnSubItems, selectedSubItemIndex, activeItem]);

    const handleTopbarHamburgerIcon = () => {
        var windowSize = document.documentElement.clientWidth;
        let sidebarOverlay = document.getElementById("sidebar-overlay") as any;
        let appMenu = (document as Document | any).querySelector(".app-menu");


        if (windowSize < 768) {
            document.body.classList.add("overflow-hidden");
            // Use smooth transitions instead of hidden class
            if (sidebarOverlay.classList.contains("pointer-events-none")) {
                sidebarOverlay.classList.remove("opacity-0", "pointer-events-none");
                sidebarOverlay.classList.add("opacity-100");
                appMenu.classList.remove("-translate-x-full", "opacity-0");
                appMenu.classList.add("translate-x-0", "opacity-100");
            } else {
                sidebarOverlay.classList.add("opacity-0", "pointer-events-none");
                sidebarOverlay.classList.remove("opacity-100");
                appMenu.classList.add("-translate-x-full", "opacity-0");
                appMenu.classList.remove("translate-x-0", "opacity-100");
            }
            dispatch(changeLeftsidebarSizeType("lg"));
        } else {
            dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm"));
        }
    }

    useEffect(() => {
        // resize
        const handleResizeLayout = () => {
            var windowSize = document.documentElement.clientWidth;

            if (windowSize < 768) {
                dispatch(changeLeftsidebarSizeType("lg"));
            } else if (windowSize <= 1024) {
                if (layoutType === "vertical") {
                    dispatch(changeLeftsidebarSizeType("sm"));
                } else {
                    dispatch(changeLeftsidebarSizeType("lg"));
                }
            } else {
                dispatch(changeLeftsidebarSizeType("lg"));

                // dispatch(changeLeftsidebarSizeType(layoutSidebarSizeType === "sm" ? "lg" : "sm"));
            }
        }

        // out side click
        const outerSideElement = () => {
            var windowSize = document.documentElement.clientWidth;
            var sidebarOverlay = document.getElementById("sidebar-overlay") as any;
            if (sidebarOverlay) {
                sidebarOverlay.addEventListener("click", () => {
                    if (!sidebarOverlay.classList.contains("pointer-events-none")) {

                        if (windowSize <= 768) {
                            let appMenu = document?.querySelector(".app-menu");
                            appMenu?.classList.add("-translate-x-full", "opacity-0");
                            appMenu?.classList.remove("translate-x-0", "opacity-100");
                            document.body.classList.remove("overflow-hidden");
                            sidebarOverlay.classList.add("opacity-0", "pointer-events-none");
                            sidebarOverlay.classList.remove("opacity-100");
                        } else {
                            dispatch(changeLeftsidebarSizeType("lg"));
                        }
                    }
                });
            }
        }

        // scroll
        const scrollNavigation = () => {
            var scrollUp = document.documentElement.scrollTop;
            if (scrollUp >= 50) {
                document.getElementById("page-topbar")?.classList.add('is-sticky');
            } else {
                document.getElementById("page-topbar")?.classList.remove('is-sticky');
            }
        };

        window.addEventListener('scroll', scrollNavigation, true);
        window.addEventListener("click", outerSideElement);
        window.addEventListener("resize", handleResizeLayout);

        // Cleanup function to remove the event listeners
        return () => {
            window.removeEventListener('scroll', scrollNavigation, true);
            window.removeEventListener("click", outerSideElement);
            window.removeEventListener("resize", handleResizeLayout);
        };
    }, [layoutType, dispatch]);

    const selectProperties = Reselect.createSelector(
        (state: any) => state.Profile,
        (profile) => ({
            user: profile.user || {},
            branding: profile.branding || null
        })
    );

    const { user, branding } = useSelector(selectProperties);

    // Dynamic logo URLs with fallbacks
    const logoSm = branding?.logo_sm || branding?.logo || defaultLogoSm;
    const logoDark = branding?.logo_dark || branding?.logo || defaultLogoDark;
    const logoLight = branding?.logo_light || branding?.logo || defaultLogoLight;
    const userProfile = user?.profile_image || defaultUserProfile;
    const favicon = branding?.favicon || branding?.logo || defaultLogoSm;

    return (
        <React.Fragment>
            <header id="page-topbar" className="ltr:md:left-vertical-menu rtl:md:right-vertical-menu group-data-[sidebar-size=md]:ltr:md:left-vertical-menu-md group-data-[sidebar-size=md]:rtl:md:right-vertical-menu-md group-data-[sidebar-size=sm]:ltr:md:left-vertical-menu-sm group-data-[sidebar-size=sm]:rtl:md:right-vertical-menu-sm group-data-[layout=horizontal]:ltr:left-0 group-data-[layout=horizontal]:rtl:right-0 fixed right-0 z-[1000] left-0 print:hidden group-data-[navbar=bordered]:m-4 group-data-[navbar=bordered]:[&.is-sticky]:mt-0 transition-all ease-linear duration-300 group-data-[navbar=hidden]:hidden group-data-[navbar=scroll]:absolute group/topbar group-data-[layout=horizontal]:z-[1004]">
                <div className="layout-width">
                    <div className="flex items-center px-4 mx-auto bg-topbar border-b-2 border-topbar group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-brand shadow-md h-header shadow-slate-200/50 group-data-[navbar=bordered]:rounded-md group-data-[navbar=bordered]:group-[.is-sticky]/topbar:rounded-t-none group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-700 dark:shadow-none group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-zink-500 group-data-[topbar=dark]:group-[.is-sticky]/topbar:dark:shadow-md group-data-[navbar=bordered]:shadow-none group-data-[layout=horizontal]:group-data-[navbar=bordered]:rounded-b-none group-data-[layout=horizontal]:shadow-none group-data-[layout=horizontal]:dark:group-[.is-sticky]/topbar:shadow-none">
                        <div className="flex items-center w-full group-data-[layout=horizontal]:mx-auto group-data-[layout=horizontal]:max-w-screen-2xl navbar-header group-data-[layout=horizontal]:ltr:xl:pr-3 group-data-[layout=horizontal]:rtl:xl:pl-3">

                            <div className="items-center justify-center hidden px-5 text-center h-header group-data-[layout=horizontal]:md:flex group-data-[layout=horizontal]:ltr::pl-0 group-data-[layout=horizontal]:rtl:pr-0">
                                <Link to="/">
                                    <span className="hidden">
                                        <img src={logoSm} alt="" className="h-8 mx-auto" />
                                    </span>
                                    <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                                        <img src={logoDark} alt="" className="h-6 mx-auto" />
                                    </span>
                                </Link>
                                <Link to="/" className="hidden group-data-[topbar=dark]:block group-data-[topbar=brand]:block">
                                    <span className="group-data-[topbar=dark]:hidden group-data-[topbar=brand]:hidden">
                                        <img src={logoSm} alt="" className="h-8 mx-auto" />
                                    </span>
                                    <span className="group-data-[topbar=dark]:block group-data-[topbar=brand]:block">
                                        <img src={logoLight} alt="" className="h-6 mx-auto" />
                                    </span>
                                </Link>
                            </div>

                            <button
                                onClick={handleTopbarHamburgerIcon}
                                type="button"
                                className="inline-flex relative justify-center items-center p-0 text-topbar-item transition-all size-[37.5px] duration-75 ease-linear bg-topbar rounded-md btn hover:bg-slate-100 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:border-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[layout=horizontal]:flex group-data-[layout=horizontal]:md:hidden hamburger-icon" id="topnav-hamburger-icon">
                                <ChevronsLeft className="w-5 h-5 group-data-[sidebar-size=sm]:hidden" />
                                <ChevronsRight className="hidden w-5 h-5 group-data-[sidebar-size=sm]:block" />
                            </button>

                            <div ref={searchDropdownRef} className="relative hidden ltr:ml-3 rtl:mr-3 lg:block group-data-[layout=horizontal]:hidden group-data-[layout=horizontal]:lg:block">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    onFocus={() => setIsSearchFocused(true)}
                                    className="py-2 pr-10 text-sm text-topbar-item bg-topbar border border-topbar-border rounded pl-8 placeholder:text-slate-400 form-control focus-visible:outline-0 min-w-[300px] focus:border-blue-400 group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:border-topbar-border-dark group-data-[topbar=dark]:placeholder:text-slate-500 group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:border-topbar-border-brand group-data-[topbar=brand]:placeholder:text-blue-300 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:border-zink-500 group-data-[topbar=dark]:dark:text-zink-100"
                                    placeholder="Search modules or customers... (Ctrl+K)"
                                    autoComplete="off"
                                />
                                <Search className="inline-block size-4 absolute left-2.5 top-2.5 text-topbar-item fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:dark:fill-zink-600" />
                                {searchQuery && (
                                    <button
                                        onClick={clearSearch}
                                        className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        <X className="size-4" />
                                    </button>
                                )}

                                {/* Search Results Dropdown - Shows when there are module results OR customer suggestions */}
                                {isSearchFocused && (searchResults.length > 0 || customerSuggestions.length > 0 || isLoadingParties) && (
                                    <div className={`absolute top-full left-0 mt-2 bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-600 rounded-lg shadow-lg flex overflow-hidden z-50 transition-all duration-200 ${activeItem?.subItems && activeItem.subItems.length > 0 ? 'w-[600px]' : 'w-[400px]'}`}>
                                        {/* Left Side: Search Results */}
                                        <div className={`${activeItem?.subItems && activeItem.subItems.length > 0 ? 'w-1/2 border-r' : 'w-full'} border-slate-200 dark:border-zink-600 overflow-y-auto max-h-[450px]`}>
                                            {/* Module Results Section */}
                                            {searchResults.length > 0 && (
                                                <>
                                                    <div className="px-4 py-2 bg-slate-50 dark:bg-zink-800 border-b border-slate-200 dark:border-zink-600">
                                                        <p className="text-xs font-semibold text-slate-500 dark:text-zink-400 uppercase tracking-wider">
                                                            Modules
                                                        </p>
                                                    </div>
                                                    {searchResults.map((item, index) => (
                                                        <button
                                                            key={item.id}
                                                            onClick={() => handleSearchItemClick(item)}
                                                            onMouseEnter={() => {
                                                                setSelectedIndex(index);
                                                                setActiveItem(item);
                                                                setFocusOnParties(false);
                                                                setSelectedCustomerIndex(-1);
                                                            }}
                                                            className={`w-full text-left px-4 py-3 transition-colors border-b border-slate-100 dark:border-zink-600 last:border-b-0 ${!focusOnSubItems && !focusOnParties && (index === selectedIndex || (activeItem && activeItem.id === item.id))
                                                                ? 'bg-blue-50 dark:bg-blue-900/20'
                                                                : 'hover:bg-slate-50 dark:hover:bg-zink-600'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex-shrink-0 text-slate-500 dark:text-zink-400">
                                                                    {item.icon}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-sm font-medium text-slate-900 dark:text-zink-50 truncate">
                                                                        {item.label}
                                                                    </p>
                                                                    <p className="text-xs text-slate-500 dark:text-zink-400 truncate">
                                                                        {item.fullLabel}
                                                                    </p>
                                                                </div>
                                                                {item.subItems && item.subItems.length > 0 && (
                                                                    <ChevronsRight className="size-4 text-slate-400" />
                                                                )}
                                                            </div>
                                                        </button>
                                                    ))}
                                                </>
                                            )}

                                            {/* Customer Suggestions Section */}
                                            {(customerSuggestions.length > 0 || isLoadingParties) && (
                                                <>
                                                    <div className="px-4 py-2 bg-slate-50 dark:bg-zink-800 border-b border-slate-200 dark:border-zink-600 flex items-center justify-between">
                                                        <p className="text-xs font-semibold text-slate-500 dark:text-zink-400 uppercase tracking-wider">
                                                            Customers {focusOnParties && <span className="text-blue-500">(Tab to switch)</span>}
                                                        </p>
                                                        {isLoadingParties && (
                                                            <div className="size-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                                        )}
                                                    </div>
                                                    {customerSuggestions.map((customer, index) => (
                                                        <button
                                                            key={customer.id}
                                                            onClick={() => handlecustomerSuggestionClick(customer)}
                                                            onMouseEnter={() => {
                                                                setFocusOnParties(true);
                                                                setSelectedCustomerIndex(index);
                                                                setFocusOnSubItems(false);
                                                            }}
                                                            className={`w-full text-left px-4 py-3 transition-colors border-b border-slate-100 dark:border-zink-600 last:border-b-0 ${focusOnParties && index === selectedCustomerIndex
                                                                ? 'bg-green-50 dark:bg-green-900/20'
                                                                : 'hover:bg-slate-50 dark:hover:bg-zink-600'
                                                                }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex-shrink-0 text-green-500 dark:text-green-400">
                                                                    {getCustomerTypeIcon(customer.customerMainTypeId)}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <p className="text-sm font-medium text-slate-900 dark:text-zink-50 truncate">
                                                                            {customer.customerName}
                                                                        </p>
                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${customer.customerMainTypeId === CUSTOMER_MAIN_TYPE_ID.PRIMARY ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                                            customer.customerMainTypeId === CUSTOMER_MAIN_TYPE_ID.SECONDARY ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                                                                customer.customerMainTypeId === CUSTOMER_MAIN_TYPE_ID.TERTIARY ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                                                                                    'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400'
                                                                            }`}>
                                                                            {getCustomerTypeLabel(customer.customerMainTypeId)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zink-400">
                                                                        <span>{customer.mobile}</span>
                                                                        {customer.city && <span>• {customer.city}</span>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                    {!isLoadingParties && customerSuggestions.length === 0 && searchQuery.length >= 2 && (
                                                        <div className="px-4 py-3 text-sm text-slate-500 dark:text-zink-400 text-center">
                                                            No customers found
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Right Side: Sub-items Preview */}
                                        {activeItem?.subItems && activeItem.subItems.length > 0 && (
                                            <div className={`w-1/2 bg-slate-50 dark:bg-zink-800/50 overflow-y-auto max-h-[450px] p-2 ${focusOnSubItems ? 'ring-2 ring-blue-400 dark:ring-blue-500 ring-inset' : ''}`}>
                                                <div className="space-y-1">
                                                    <p className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-zink-400 uppercase tracking-wider">
                                                        Sub-Modules {focusOnSubItems && '(Press Enter to select)'}
                                                    </p>
                                                    {activeItem.subItems.map((subItem: any, subIndex: number) => (
                                                        <button
                                                            key={subItem.id}
                                                            onClick={() => handleSubItemClick(subItem)}
                                                            onMouseEnter={() => {
                                                                if (focusOnSubItems) {
                                                                    setSelectedSubItemIndex(subIndex);
                                                                }
                                                            }}
                                                            className={`w-full text-left px-3 py-2 rounded-md transition-all flex items-center gap-2 ${focusOnSubItems && subIndex === selectedSubItemIndex
                                                                ? 'bg-blue-100 dark:bg-blue-900/40 shadow-md ring-2 ring-blue-400 dark:ring-blue-500'
                                                                : 'hover:bg-white dark:hover:bg-zink-700 hover:shadow-sm'
                                                                }`}
                                                        >
                                                            <div className="text-slate-400 transition-colors">
                                                                {subItem.icon || <ChevronsRight size={14} />}
                                                            </div>
                                                            <span className="text-sm text-slate-700 dark:text-zink-200 font-medium">
                                                                {subItem.label}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* No Results Message */}
                                {isSearchFocused && searchQuery && searchResults.length === 0 && customerSuggestions.length === 0 && !isLoadingParties && searchQuery.length >= 2 && (
                                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-zink-700 border border-slate-200 dark:border-zink-600 rounded-lg shadow-lg p-4 z-50">
                                        <p className="text-sm text-slate-500 dark:text-zink-400 text-center">
                                            No results found for "{searchQuery}"
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 ms-auto">

                                {/* Update Button - Shows when new version available */}
                                <UpdateButton />

                                {/* Background Jobs Dropdown */}
                                <BackgroundJobsDropdown />

                                {/* LightDark */}
                                <LightDark />

                                {/* Notification Sidebar Toggle */}
                                {handleToggleNotification ? (
                                  <div className="relative flex items-center h-header">
                                    <button
                                      type="button"
                                      onClick={handleToggleNotification}
                                      className="inline-flex justify-center relative items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar rounded-md btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200"
                                    >
                                      <BellRing className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand" />
                                    </button>
                                  </div>
                                ) : (
                                  <NotificationDropdown />
                                )}

                                <div className="relative items-center hidden h-header md:flex">
                                    <button onClick={handleToggleDrawer} data-drawer-target="customizerButton" type="button" className="inline-flex justify-center items-center p-0 text-topbar-item transform  transition-all size-[37.5px] duration-300 ease-in-out bg-topbar group-data-[topbar=dark]:text-topbar-item-dark rounded-md btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200">
                                        <Settings className="inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand"></Settings>
                                    </button>
                                </div>

                                <Dropdown className="relative flex items-center h-header">
                                    <Dropdown.Trigger type="button" className="flex items-center gap-3 p-1 transition-all duration-200 ease-linear bg-topbar rounded-md text-topbar-item dropdown-toggle btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:text-topbar-item-dark group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200" id="dropdownMenuButton" data-bs-toggle="dropdown">
                                        <div className="size-[50px] rounded-full overflow-hidden flex items-center justify-center">
                                            <Lottie animationData={profileAnimation} loop={true} style={{ width: 60, height: 60 }} />
                                        </div>
                                        <span className="hidden mr-2 font-medium xl:block">{user.name || "Admin"}</span>
                                    </Dropdown.Trigger>
                                    <Dropdown.Content placement="right-end" className="absolute z-50 p-4 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[16rem] dark:bg-zink-600" aria-labelledby="dropdownMenuButton">
                                        <h6 className="mb-2 text-sm font-normal text-slate-500 dark:text-zink-300">{branding?.welcome_message || "Welcome"}</h6>
                                        <a href="#!" className="flex gap-3 mb-3">
                                            <div className="relative inline-block shrink-0">
                                                <div className="rounded bg-slate-100 dark:bg-zink-500">
                                                    <img src={favicon} alt="" className="size-12 rounded" />
                                                </div>
                                                <span className="-top-1 ltr:-right-1 rtl:-left-1 absolute size-2.5 bg-green-400 border-2 border-white rounded-full dark:border-zink-600"></span>
                                            </div>
                                            <div>
                                                <h6 className="mb-1 text-15">{user.name || "admin"}</h6>
                                                <p className="text-slate-500 dark:text-zink-300">{user.loginTypeName || "Admin"}</p>
                                            </div>
                                        </a>
                                        <ul>
                                            <li>
                                                <ProfileLink className="block py-2 text-sm font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500">
                                                    <div className="flex items-center gap-2">
                                                        <User className="size-4" />
                                                        <span className="whitespace-nowrap">My Profile</span>
                                                    </div>
                                                </ProfileLink>
                                            </li>
                                            <li className="pt-2 mt-2 border-t border-slate-200 dark:border-zink-500">
                                                <a className="block py-2 text-sm font-medium transition-all duration-200 ease-linear text-slate-600 dropdown-item hover:text-custom-500 focus:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:focus:text-custom-500" href={process.env.PUBLIC_URL + "/logout"}>
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-2 flex-shrink-0">
                                                            <LogOut className="size-4" />
                                                            <span className="whitespace-nowrap">Sign Out</span>
                                                        </div>
                                                        <kbd className="px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-300 rounded dark:bg-zink-700 dark:border-zink-600 dark:text-zink-300 whitespace-nowrap ml-auto">
                                                            Ctrl+Shift+Q
                                                        </kbd>
                                                    </div>
                                                </a>
                                            </li>
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </React.Fragment>
    );
};

export default Header;
